import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, IconButton, Button, useDisclosure, useToast, Input,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Select, VStack, Text, Avatar,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  DrawerFooter, Divider, Box, Textarea, SimpleGrid, Tabs, TabList, TabPanels, Tab, TabPanel,
  Switch,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon, ViewIcon, EditIcon, DeleteIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import guestService from '../services/guestService';

const VIP_COLORS = { 0: 'gray', 1: 'green', 2: 'blue', 3: 'purple', 4: 'orange', 5: 'yellow' };
// Translation keys per VIP level; translated where rendered.
const VIP_LABEL_KEYS = {
  0: 'guestManagement.vip.regular', 1: 'guestManagement.vip.silver', 2: 'guestManagement.vip.gold',
  3: 'guestManagement.vip.platinum', 4: 'guestManagement.vip.diamond', 5: 'guestManagement.vip.vip',
};

const emptyGuestForm = {
  title: '', first_name: '', last_name: '', email: '', phone: '',
  date_of_birth: '', nationality: '', vip_level: 0, notes: '',
};

const emptyAddressForm = {
  address_type: 'residential', street_address: '', city: '',
  state_province: '', postal_code: '', country: '', is_primary: true,
};

const emptyIdForm = {
  id_type: 'passport', id_number: '', issuing_country: '', expiry_date: '',
};

const PAGE_SIZE = 20;

const GuestManagement = () => {
  const { t } = useTranslation();
  const [guests, setGuests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // searchInput = what's in the box; search = the applied (debounced) query.
  // Splitting them stops the table reloading on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestForm, setGuestForm] = useState(emptyGuestForm);
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [idForm, setIdForm] = useState(emptyIdForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const hasLoadedOnce = useRef(false);

  const detailDisclosure = useDisclosure();
  const guestModalDisclosure = useDisclosure();
  const addressModalDisclosure = useDisclosure();
  const idModalDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const load = useCallback(async () => {
    try {
      // Full-page spinner only on the very first load; searches and page
      // flips just swap the table contents in place.
      if (!hasLoadedOnce.current) setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      const data = await guestService.getAll(params);
      if (data?.results) {
        setGuests(data.results);
        setTotalCount(data.count || 0);
      } else {
        const list = Array.isArray(data) ? data : [];
        setGuests(list);
        setTotalCount(list.length);
      }
      hasLoadedOnce.current = true;
    } catch { setError(t('guestManagement.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [search, page, t]);

  useEffect(() => { load(); }, [load]);

  // Debounce typing -> applied search (400ms of quiet). Same-value sets are
  // no-ops for React state, so this fires a reload only when the query
  // actually changed.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // --- View guest detail ---
  const viewGuest = async (guest) => {
    try {
      const full = await guestService.getById(guest.id);
      setSelectedGuest(full);
    } catch {
      setSelectedGuest(guest);
    }
    detailDisclosure.onOpen();
  };

  // --- Create / Edit guest ---
  const openCreateGuest = () => {
    setGuestForm(emptyGuestForm);
    setEditingGuestId(null);
    guestModalDisclosure.onOpen();
  };

  const openEditGuest = (guest) => {
    setGuestForm({
      title: guest.title || '',
      first_name: guest.first_name || '',
      last_name: guest.last_name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      date_of_birth: guest.date_of_birth || '',
      nationality: guest.nationality || '',
      vip_level: guest.vip_level ?? 0,
      notes: guest.notes || '',
    });
    setEditingGuestId(guest.id);
    guestModalDisclosure.onOpen();
  };

  const handleSaveGuest = async () => {
    setSaving(true);
    try {
      const payload = { ...guestForm };
      // Convert empty strings to null for optional fields
      if (!payload.date_of_birth) payload.date_of_birth = null;
      if (!payload.email) payload.email = null;

      if (editingGuestId) {
        await guestService.update(editingGuestId, payload);
        toast({ title: t('guestManagement.toasts.guestUpdated'), status: 'success', duration: 2000 });
      } else {
        const hotelId = localStorage.getItem('admin_hotel_id');
        await guestService.create({ ...payload, hotel: hotelId ? Number(hotelId) : undefined });
        toast({ title: t('guestManagement.toasts.guestCreated'), status: 'success', duration: 2000 });
      }
      guestModalDisclosure.onClose();
      load();
      // Refresh drawer if open
      if (selectedGuest && editingGuestId === selectedGuest.id) {
        const refreshed = await guestService.getById(editingGuestId);
        setSelectedGuest(refreshed);
      }
    } catch (err) {
      toast({ title: t('guestManagement.toasts.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  // --- Delete guest ---
  const handleDeleteGuest = async () => {
    try {
      await guestService.remove(deleteTarget.id);
      toast({ title: t('guestManagement.toasts.guestRemoved'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose();
      if (detailDisclosure.isOpen) detailDisclosure.onClose();
      load();
    } catch {
      toast({ title: t('guestManagement.toasts.guestRemoveFailed'), status: 'error', duration: 3000 });
    }
  };

  // --- Blacklist toggle ---
  const handleToggleBlacklist = async (guest) => {
    try {
      await guestService.update(guest.id, { is_blacklisted: !guest.is_blacklisted });
      toast({ title: guest.is_blacklisted ? t('guestManagement.toasts.removedFromBlacklist') : t('guestManagement.toasts.blacklisted'), status: 'info', duration: 2000 });
      load();
      if (selectedGuest?.id === guest.id) {
        const refreshed = await guestService.getById(guest.id);
        setSelectedGuest(refreshed);
      }
    } catch { toast({ title: t('guestManagement.toasts.failed'), status: 'error', duration: 3000 }); }
  };

  // --- Add / edit / delete address ---
  const openAddAddress = () => {
    setAddressForm(emptyAddressForm);
    setEditingAddressId(null);
    addressModalDisclosure.onOpen();
  };

  const openEditAddress = (address) => {
    setAddressForm({
      address_type: address.address_type || 'residential',
      street_address: address.street_address || '',
      city: address.city || '',
      state_province: address.state_province || '',
      postal_code: address.postal_code || '',
      country: address.country || '',
      is_primary: address.is_primary ?? false,
    });
    setEditingAddressId(address.id);
    addressModalDisclosure.onOpen();
  };

  const handleSaveAddress = async () => {
    setSaving(true);
    try {
      if (editingAddressId) {
        await guestService.updateAddress(selectedGuest.id, editingAddressId, addressForm);
        toast({ title: t('guestManagement.toasts.addressUpdated'), status: 'success', duration: 2000 });
      } else {
        await guestService.addAddress(selectedGuest.id, addressForm);
        toast({ title: t('guestManagement.toasts.addressAdded'), status: 'success', duration: 2000 });
      }
      addressModalDisclosure.onClose();
      const refreshed = await guestService.getById(selectedGuest.id);
      setSelectedGuest(refreshed);
    } catch (err) {
      toast({ title: t('guestManagement.toasts.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  const handleDeleteAddress = async (address) => {
    if (!window.confirm(t('guestManagement.confirm.deleteAddress', { type: address.address_type }))) return;
    try {
      await guestService.removeAddress(selectedGuest.id, address.id);
      toast({ title: t('guestManagement.toasts.addressDeleted'), status: 'success', duration: 2000 });
      const refreshed = await guestService.getById(selectedGuest.id);
      setSelectedGuest(refreshed);
    } catch {
      toast({ title: t('guestManagement.toasts.addressDeleteFailed'), status: 'error', duration: 3000 });
    }
  };

  // --- Add identification ---
  const openAddId = () => {
    setIdForm(emptyIdForm);
    idModalDisclosure.onOpen();
  };

  const handleSaveId = async () => {
    if (!idForm.id_number || !idForm.issuing_country || !idForm.expiry_date) {
      toast({ title: t('guestManagement.toasts.fillRequired'), status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      await guestService.addIdentification(selectedGuest.id, idForm);
      toast({ title: t('guestManagement.toasts.idAdded'), status: 'success', duration: 2000 });
      idModalDisclosure.onClose();
      const refreshed = await guestService.getById(selectedGuest.id);
      setSelectedGuest(refreshed);
    } catch (err) {
      toast({ title: t('guestManagement.toasts.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  const setG = (f) => (e) => setGuestForm({ ...guestForm, [f]: e.target.value });
  const setA = (f) => (e) => setAddressForm({ ...addressForm, [f]: e.target.value });
  const setI = (f) => (e) => setIdForm({ ...idForm, [f]: e.target.value });

  return (
    <PageShell
      title={t('guestManagement.title')}
      subtitle={t('guestManagement.subtitle', { count: totalCount })}
      breadcrumbs={[{ label: t('guestManagement.breadcrumb') }]}
      loading={loading} error={error}
      actions={
        <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreateGuest}>
          {t('guestManagement.buttons.addGuest')}
        </Button>
      }
    >
      {/* Search */}
      <HStack mb={4}>
        <Input size="sm" placeholder={t('guestManagement.search.placeholder')} value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)} maxW="400px"
          onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); } }}
        />
        <Button size="sm" leftIcon={<SearchIcon />}
          onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>{t('guestManagement.search.button')}</Button>
      </HStack>

      {/* Guest Table */}
      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>{t('guestManagement.columns.guest')}</Th><Th>{t('guestManagement.fields.email')}</Th><Th>{t('guestManagement.fields.phone')}</Th><Th>{t('guestManagement.columns.vip')}</Th>
                  <Th>{t('guestManagement.fields.nationality')}</Th><Th>{t('guestManagement.columns.status')}</Th><Th>{t('guestManagement.columns.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {guests.map(g => (
                  <Tr key={g.id} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => viewGuest(g)}>
                    <Td>
                      <HStack>
                        <Avatar size="xs" name={g.full_name || `${g.first_name} ${g.last_name}`} />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="500">{g.full_name || `${g.first_name} ${g.last_name}`}</Text>
                          <Text fontSize="2xs" color="gray.400">{g.guest_id}</Text>
                        </VStack>
                      </HStack>
                    </Td>
                    <Td fontSize="sm">{g.email || '-'}</Td>
                    <Td fontSize="sm">{g.phone || '-'}</Td>
                    <Td>
                      <Badge colorScheme={VIP_COLORS[g.vip_level] || 'gray'} fontSize="xs">
                        {t(VIP_LABEL_KEYS[g.vip_level] || VIP_LABEL_KEYS[0])}
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{g.nationality || '-'}</Td>
                    <Td>
                      <Badge colorScheme={g.is_blacklisted ? 'red' : 'green'} fontSize="xs">
                        {g.is_blacklisted ? t('guestManagement.status.blacklisted') : t('guestManagement.status.active')}
                      </Badge>
                    </Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <HStack spacing={1}>
                        <IconButton size="xs" icon={<ViewIcon />} variant="ghost" onClick={() => viewGuest(g)} aria-label={t('guestManagement.actions.view')} />
                        <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEditGuest(g)} aria-label={t('guestManagement.actions.edit')} />
                        <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                          onClick={() => { setDeleteTarget(g); deleteDisclosure.onOpen(); }} aria-label={t('guestManagement.actions.delete')} />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {guests.length === 0 && (
                  <Tr><Td colSpan={7} textAlign="center" py={8} color="gray.400">{t('guestManagement.emptyState.noGuests')}</Td></Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="space-between" mt={4}>
          <Text fontSize="sm" color="gray.500">
            {t('guestManagement.pagination.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, totalCount), total: totalCount })}
          </Text>
          <HStack spacing={2}>
            <IconButton size="sm" icon={<ChevronLeftIcon />} variant="outline"
              isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label={t('guestManagement.pagination.previous')} />
            <Text fontSize="sm" fontWeight="500">{t('guestManagement.pagination.pageOf', { page, totalPages })}</Text>
            <IconButton size="sm" icon={<ChevronRightIcon />} variant="outline"
              isDisabled={page >= totalPages} onClick={() => setPage(p => p + 1)} aria-label={t('guestManagement.pagination.next')} />
          </HStack>
        </HStack>
      )}

      {/* ===== Guest Detail Drawer ===== */}
      <Drawer isOpen={detailDisclosure.isOpen} onClose={detailDisclosure.onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{t('guestManagement.profile.title')}</DrawerHeader>
          <DrawerBody>
            {selectedGuest && (
              <VStack align="stretch" spacing={4}>
                {/* Header */}
                <HStack spacing={4}>
                  <Avatar size="lg" name={selectedGuest.full_name || `${selectedGuest.first_name} ${selectedGuest.last_name}`} />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontWeight="600" fontSize="lg">
                      {selectedGuest.title ? `${selectedGuest.title} ` : ''}
                      {selectedGuest.full_name || `${selectedGuest.first_name} ${selectedGuest.last_name}`}
                    </Text>
                    <Text fontSize="sm" color="gray.500">{selectedGuest.guest_id}</Text>
                    <HStack mt={1}>
                      <Badge colorScheme={VIP_COLORS[selectedGuest.vip_level]}>
                        {VIP_LABEL_KEYS[selectedGuest.vip_level] ? t(VIP_LABEL_KEYS[selectedGuest.vip_level]) : null}
                      </Badge>
                      {selectedGuest.is_blacklisted && <Badge colorScheme="red">{t('guestManagement.status.blacklisted')}</Badge>}
                    </HStack>
                  </VStack>
                  <IconButton icon={<EditIcon />} size="sm" variant="ghost"
                    onClick={() => openEditGuest(selectedGuest)} aria-label={t('guestManagement.actions.editGuest')} />
                </HStack>

                <Divider />

                {/* Tabs */}
                <Tabs size="sm" variant="enclosed">
                  <TabList>
                    <Tab>{t('guestManagement.profile.tabs.contact')}</Tab>
                    <Tab>{t('guestManagement.profile.tabs.addresses', { count: selectedGuest.addresses?.length || 0 })}</Tab>
                    <Tab>{t('guestManagement.profile.tabs.ids', { count: selectedGuest.identifications?.length || 0 })}</Tab>
                    <Tab>{t('guestManagement.profile.tabs.stays', { count: selectedGuest.stay_history?.length || 0 })}</Tab>
                  </TabList>
                  <TabPanels>
                    {/* Contact Tab */}
                    <TabPanel px={0}>
                      <SimpleGrid columns={2} spacing={3}>
                        <Box><Text fontSize="xs" color="gray.400">{t('guestManagement.fields.email')}</Text><Text fontSize="sm">{selectedGuest.email || '-'}</Text></Box>
                        <Box><Text fontSize="xs" color="gray.400">{t('guestManagement.fields.phone')}</Text><Text fontSize="sm">{selectedGuest.phone || '-'}</Text></Box>
                        <Box><Text fontSize="xs" color="gray.400">{t('guestManagement.fields.nationality')}</Text><Text fontSize="sm">{selectedGuest.nationality || '-'}</Text></Box>
                        <Box><Text fontSize="xs" color="gray.400">{t('guestManagement.fields.dateOfBirth')}</Text><Text fontSize="sm">{selectedGuest.date_of_birth || '-'}</Text></Box>
                      </SimpleGrid>
                      {selectedGuest.notes && (
                        <Box mt={3}>
                          <Text fontSize="xs" color="gray.400">{t('guestManagement.fields.notes')}</Text>
                          <Text fontSize="sm">{selectedGuest.notes}</Text>
                        </Box>
                      )}
                      {selectedGuest.loyalty && (
                        <Box mt={3} p={3} borderWidth="1px" borderRadius="md" bg="blue.50">
                          <Text fontSize="xs" fontWeight="600" color="blue.600">{t('guestManagement.profile.loyaltyProgram')}</Text>
                          <HStack mt={1}>
                            <Badge colorScheme="blue">{selectedGuest.loyalty.tier}</Badge>
                            <Text fontSize="sm">{t('guestManagement.profile.loyaltyPoints', { count: selectedGuest.loyalty.points })}</Text>
                            <Text fontSize="xs" color="gray.400">{t('guestManagement.profile.membershipId', { id: selectedGuest.loyalty.membership_id })}</Text>
                          </HStack>
                        </Box>
                      )}
                    </TabPanel>

                    {/* Addresses Tab */}
                    <TabPanel px={0}>
                      <Button size="xs" leftIcon={<AddIcon />} colorScheme="blue" mb={3} onClick={openAddAddress}>
                        {t('guestManagement.buttons.addAddress')}
                      </Button>
                      {(selectedGuest.addresses || []).map((a, i) => (
                        <Box key={a.id || i} p={3} borderWidth="1px" borderRadius="md" mb={2}>
                          <HStack justify="space-between" mb={1}>
                            <HStack spacing={1}>
                              <Badge fontSize="2xs">{a.address_type}</Badge>
                              {a.is_primary && <Badge colorScheme="blue" fontSize="2xs">{t('guestManagement.profile.primary')}</Badge>}
                            </HStack>
                            <HStack spacing={0}>
                              <IconButton size="xs" icon={<EditIcon />} variant="ghost"
                                onClick={() => openEditAddress(a)} aria-label={t('guestManagement.actions.editAddress')} />
                              <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                                onClick={() => handleDeleteAddress(a)} aria-label={t('guestManagement.actions.deleteAddress')} />
                            </HStack>
                          </HStack>
                          <Text fontSize="sm">{a.street_address}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {[a.city, a.state_province, a.postal_code, a.country].filter(Boolean).join(', ')}
                          </Text>
                        </Box>
                      ))}
                      {(!selectedGuest.addresses || selectedGuest.addresses.length === 0) && (
                        <Text fontSize="sm" color="gray.400">{t('guestManagement.emptyState.noAddresses')}</Text>
                      )}
                    </TabPanel>

                    {/* Identifications Tab */}
                    <TabPanel px={0}>
                      <Button size="xs" leftIcon={<AddIcon />} colorScheme="blue" mb={3} onClick={openAddId}>
                        {t('guestManagement.buttons.addId')}
                      </Button>
                      {(selectedGuest.identifications || []).map((id, i) => (
                        <Box key={i} p={3} borderWidth="1px" borderRadius="md" mb={2}>
                          <HStack justify="space-between" mb={1}>
                            <Badge fontSize="2xs">{id.id_type}</Badge>
                            {id.expiry_date && (
                              <Text fontSize="2xs" color={new Date(id.expiry_date) < new Date() ? 'red.500' : 'gray.400'}>
                                {t('guestManagement.profile.expires', { date: id.expiry_date })}
                              </Text>
                            )}
                          </HStack>
                          <Text fontSize="sm" fontWeight="500">{id.id_number}</Text>
                          <Text fontSize="xs" color="gray.500">{t('guestManagement.profile.issued', { country: id.issuing_country || '-' })}</Text>
                        </Box>
                      ))}
                      {(!selectedGuest.identifications || selectedGuest.identifications.length === 0) && (
                        <Text fontSize="sm" color="gray.400">{t('guestManagement.emptyState.noIdentifications')}</Text>
                      )}
                    </TabPanel>

                    {/* Stay History Tab */}
                    <TabPanel px={0}>
                      {(selectedGuest.stay_history || []).map((s, i) => (
                        <HStack key={i} p={3} borderWidth="1px" borderRadius="md" mb={2} justify="space-between">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="500">{t('guestManagement.profile.stayRoom', { roomType: s.room_type, roomNumber: s.room_number })}</Text>
                            <Text fontSize="xs" color="gray.500">{t('guestManagement.profile.stayDates', { checkIn: s.check_in, checkOut: s.check_out })}</Text>
                          </VStack>
                          <VStack align="end" spacing={0}>
                            <Text fontSize="sm" fontWeight="600">{s.total_amount}</Text>
                            {s.rating && <Badge colorScheme="yellow" fontSize="2xs">{s.rating}/5</Badge>}
                          </VStack>
                        </HStack>
                      ))}
                      {(!selectedGuest.stay_history || selectedGuest.stay_history.length === 0) && (
                        <Text fontSize="sm" color="gray.400">{t('guestManagement.emptyState.noStays')}</Text>
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </VStack>
            )}
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            {selectedGuest && (
              <HStack spacing={2} w="100%">
                <Button size="sm" variant="outline" onClick={() => openEditGuest(selectedGuest)}>
                  {t('guestManagement.buttons.editGuest')}
                </Button>
                <Button
                  size="sm"
                  colorScheme={selectedGuest.is_blacklisted ? 'green' : 'red'}
                  variant="outline"
                  onClick={() => handleToggleBlacklist(selectedGuest)}
                >
                  {selectedGuest.is_blacklisted ? t('guestManagement.buttons.removeBlacklist') : t('guestManagement.buttons.blacklist')}
                </Button>
                <Button size="sm" colorScheme="red" variant="ghost"
                  onClick={() => { setDeleteTarget(selectedGuest); deleteDisclosure.onOpen(); }}>
                  {t('guestManagement.buttons.delete')}
                </Button>
              </HStack>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ===== Create / Edit Guest Modal ===== */}
      <Modal isOpen={guestModalDisclosure.isOpen} onClose={guestModalDisclosure.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingGuestId ? t('guestManagement.buttons.editGuest') : t('guestManagement.modals.addGuestTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack w="100%" spacing={4}>
                <FormControl maxW="100px">
                  <FormLabel fontSize="sm">{t('guestManagement.fields.title')}</FormLabel>
                  <Select size="sm" value={guestForm.title} onChange={setG('title')}>
                    <option value="">-</option>
                    <option value="Mr">{t('guestManagement.titles.mr')}</option>
                    <option value="Mrs">{t('guestManagement.titles.mrs')}</option>
                    <option value="Ms">{t('guestManagement.titles.ms')}</option>
                    <option value="Dr">{t('guestManagement.titles.dr')}</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.firstName')}</FormLabel>
                  <Input size="sm" value={guestForm.first_name} onChange={setG('first_name')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.lastName')}</FormLabel>
                  <Input size="sm" value={guestForm.last_name} onChange={setG('last_name')} />
                </FormControl>
              </HStack>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.email')}</FormLabel>
                  <Input size="sm" type="email" value={guestForm.email} onChange={setG('email')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.phone')}</FormLabel>
                  <Input size="sm" value={guestForm.phone} onChange={setG('phone')} />
                </FormControl>
              </HStack>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.dateOfBirth')}</FormLabel>
                  <Input size="sm" type="date" value={guestForm.date_of_birth} onChange={setG('date_of_birth')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.nationality')}</FormLabel>
                  <Input size="sm" value={guestForm.nationality} onChange={setG('nationality')} placeholder={t('guestManagement.form.nationalityPlaceholder')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.vipLevel')}</FormLabel>
                  <Select size="sm" value={guestForm.vip_level} onChange={setG('vip_level')}>
                    {Object.entries(VIP_LABEL_KEYS).map(([k, labelKey]) => (
                      <option key={k} value={k}>{t(labelKey)}</option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">{t('guestManagement.fields.notes')}</FormLabel>
                <Textarea size="sm" value={guestForm.notes} onChange={setG('notes')} rows={2}
                  placeholder={t('guestManagement.form.notesPlaceholder')} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={guestModalDisclosure.onClose}>{t('guestManagement.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSaveGuest} isLoading={saving}>
              {editingGuestId ? t('guestManagement.buttons.saveChanges') : t('guestManagement.buttons.createGuest')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ===== Add Address Modal ===== */}
      <Modal isOpen={addressModalDisclosure.isOpen} onClose={addressModalDisclosure.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingAddressId ? t('guestManagement.modals.editAddressTitle') : t('guestManagement.buttons.addAddress')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{t('guestManagement.fields.addressType')}</FormLabel>
                <Select size="sm" value={addressForm.address_type} onChange={setA('address_type')}>
                  <option value="residential">{t('guestManagement.addressTypes.residential')}</option>
                  <option value="business">{t('guestManagement.addressTypes.business')}</option>
                  <option value="billing">{t('guestManagement.addressTypes.billing')}</option>
                  <option value="other">{t('guestManagement.options.other')}</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('guestManagement.fields.streetAddress')}</FormLabel>
                <Input size="sm" value={addressForm.street_address} onChange={setA('street_address')} />
              </FormControl>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.city')}</FormLabel>
                  <Input size="sm" value={addressForm.city} onChange={setA('city')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.stateProvince')}</FormLabel>
                  <Input size="sm" value={addressForm.state_province} onChange={setA('state_province')} />
                </FormControl>
              </HStack>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.postalCode')}</FormLabel>
                  <Input size="sm" value={addressForm.postal_code} onChange={setA('postal_code')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.country')}</FormLabel>
                  <Input size="sm" value={addressForm.country} onChange={setA('country')} placeholder={t('guestManagement.form.countryPlaceholder')} />
                </FormControl>
              </HStack>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>{t('guestManagement.fields.primaryAddress')}</FormLabel>
                <Switch isChecked={!!addressForm.is_primary}
                  onChange={(e) => setAddressForm({ ...addressForm, is_primary: e.target.checked })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={addressModalDisclosure.onClose}>{t('guestManagement.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSaveAddress} isLoading={saving}>
              {editingAddressId ? t('guestManagement.buttons.saveChanges') : t('guestManagement.buttons.addAddress')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ===== Add Identification Modal ===== */}
      <Modal isOpen={idModalDisclosure.isOpen} onClose={idModalDisclosure.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('guestManagement.modals.addIdTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{t('guestManagement.fields.idType')}</FormLabel>
                <Select size="sm" value={idForm.id_type} onChange={setI('id_type')}>
                  <option value="passport">{t('guestManagement.idTypes.passport')}</option>
                  <option value="drivers_license">{t('guestManagement.idTypes.driversLicense')}</option>
                  <option value="id_card">{t('guestManagement.idTypes.idCard')}</option>
                  <option value="other">{t('guestManagement.options.other')}</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('guestManagement.fields.idNumber')}</FormLabel>
                <Input size="sm" value={idForm.id_number} onChange={setI('id_number')} />
              </FormControl>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.issuingCountry')}</FormLabel>
                  <Input size="sm" value={idForm.issuing_country} onChange={setI('issuing_country')} placeholder={t('guestManagement.form.issuingCountryPlaceholder')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('guestManagement.fields.expiryDate')}</FormLabel>
                  <Input size="sm" type="date" value={idForm.expiry_date} onChange={setI('expiry_date')} />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={idModalDisclosure.onClose}>{t('guestManagement.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSaveId} isLoading={saving}>{t('guestManagement.buttons.addId')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ===== Delete Confirmation ===== */}
      <ConfirmModal
        isOpen={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleDeleteGuest}
        title={t('guestManagement.removeConfirm.title')}
        message={t('guestManagement.removeConfirm.message', { name: `${deleteTarget?.first_name} ${deleteTarget?.last_name}` })}
        confirmLabel={t('guestManagement.removeConfirm.confirm')}
      />
    </PageShell>
  );
};

export default GuestManagement;
